import { useState, useEffect, useLayoutEffect, useRef, useContext, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Scrollbars } from 'react-custom-scrollbars-2';
import NavigationMenu from "../components/NavigationMenu"
import BrandsFilter from "../components/BrandsFilter"
import ResultEntry from "../components/ResultEntry"
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import publicacionService from '../services/publicacionService';
import searchService from '../services/searchService';
import { SearchContext } from '../context/SearchContext';
import Filters from '../components/Filters';

// Cache a nivel de módulo: persiste entre unmount/remount sin problemas de timing
const _c = { results: null, page: 0, hasMore: true, totalPages: 0, filters: null, hasSearched: false };

// El scroll real ocurre en .solo-autos (body tiene overflow-y: hidden)
const getScrollEl = () => document.querySelector('.solo-autos');

// Restaura el scrollTop sin animación (evita que scroll-behavior: smooth anime la vuelta)
const restoreScroll = (scrollEl, top) => {
  scrollEl.style.scrollBehavior = 'auto';
  scrollEl.scrollTop = top;
  scrollEl.style.scrollBehavior = '';
};


const Home = ({ filterData }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const hasCache = !!_c.results;

  const [resultEntries, setResultEntries] = useState(_c.results || []);
  const [resultAllEntries, setAllResultEntries] = useState(_c.results || []);
  const [currentPage, setCurrentPage] = useState(_c.page || 0);
  const [totalPages, setTotalPages] = useState(_c.totalPages || 0);
  const [hasMore, setHasMore] = useState(hasCache ? _c.hasMore : true);
  const [isLoading, setIsLoading] = useState(false);
  const [currentFilters, setCurrentFilters] = useState(_c.filters || null);
  const [selectedMaker, setSelectedMaker] = useState(_c.filters?.maker || '');
  const [initialLoadDone, setInitialLoadDone] = useState(hasCache);
  const { searchText } = useContext(SearchContext);
  const loadMoreRef = useRef(null);

  // Lee los filtros activos desde la URL
  const getFiltersFromUrl = () => {
    const f = {};
    ['q','maker','model','yearFrom','yearTo','priceMin','priceMax','transmission','fuel','region','city'].forEach(k => {
      const v = searchParams.get(k);
      if (v) f[k] = v;
    });
    return f;
  };

  // Escribe los filtros activos en la URL (sin page ni size)
  const updateUrl = (filters) => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '' && k !== 'page' && k !== 'size') {
        params[k] = String(v);
      }
    });
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    // Saltar si searchText coincide con lo que ya tenemos en caché.
    // Esto cubre el montaje inicial Y el doble montaje de React.StrictMode,
    // ambos casos donde NO queremos borrar el caché ni recargar datos.
    if (_c.results !== null && searchText === (_c.filters?.q ?? '')) return;
    // Saltar si no hay texto de búsqueda, sin caché, y nunca se ha buscado (la carga inicial lo maneja).
    if (!searchText && _c.results === null && !_c.hasSearched) return;

    const doSearch = async () => {
      _c.hasSearched = true;
      _c.results = null; // Limpiar caché al hacer nueva búsqueda
      // Tomar filtros del panel activos, sin q ni page
      const { q: _q, page: _p, ...panelFilters } = currentFiltersRef.current || {};
      const filters = {
        ...panelFilters,
        ...(searchText?.trim() ? { q: searchText.trim() } : {}),
        page: 0,
        size: 20
      };

      try {
        const results = await searchService.searchVehicles(filters);
        const hasMorePages = (results.number + 1) < results.totalPages;

        const loadedPage = results.number || 0;
        const nextPage = loadedPage + 1;

        setResultEntries(results.content || []);
        setAllResultEntries(results.content || []);
        setCurrentPage(nextPage);
        setTotalPages(results.totalPages || 0);
        setHasMore(hasMorePages);

        currentPageRef.current = nextPage;
        hasMoreRef.current = hasMorePages;
        isLoadingRef.current = false;
        lastLoadedPageRef.current = loadedPage;
        initialLoadDoneRef.current = true;
        setInitialLoadDone(true);

        setCurrentFilters(filters);
        currentFiltersRef.current = filters;
        updateUrl(filters);
      } catch (error) {
        console.error('Error al buscar:', error);
      }
    };

    doSearch();
  }, [searchText]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refs para acceder a los valores actuales sin causar re-renders (inicializados desde caché módulo)
  const isLoadingRef = useRef(false);
  const hasMoreRef = useRef(hasCache ? _c.hasMore : true);
  const currentPageRef = useRef(_c.page || 0);
  const currentFiltersRef = useRef(_c.filters || null);
  const initialLoadDoneRef = useRef(hasCache);
  const lastLoadedPageRef = useRef(hasCache ? _c.page - 1 : -1);

  // Sincronizar refs con estados
  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    currentFiltersRef.current = currentFilters;
  }, [currentFilters]);

  // Target de scroll a restaurar. Se usa un ref para no provocar re-renders.
  const scrollRestoreTarget = useRef(0);

  // Bloquea el IntersectionObserver mientras se está restaurando el scroll.
  // Sin esto, el observer se dispara en posición 0 (antes de restaurar) y
  // llama a loadMoreData innecesariamente, lo que parece un "reload" del listado.
  const scrollRestorationActive = useRef(false);

  // Al montar: leer el scroll guardado e intentar restaurarlo de inmediato.
  // Usamos clave fija 'home_scroll' para no depender de location.key,
  // que puede cambiar si se llamó setSearchParams en algún momento.
  useLayoutEffect(() => {
    const saved = parseInt(sessionStorage.getItem('home_scroll') || '0');
    if (saved > 0) {
      sessionStorage.removeItem('home_scroll');
      scrollRestoreTarget.current = saved;
      scrollRestorationActive.current = true;
      const scrollEl = getScrollEl();
      if (scrollEl) restoreScroll(scrollEl, saved);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Seguridad: desbloquear el observer después de 2s aunque no se haya podido restaurar
  useEffect(() => {
    if (!scrollRestorationActive.current) return;
    const t = setTimeout(() => { scrollRestorationActive.current = false; }, 2000);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-intentar el scroll cada vez que carguen más resultados.
  useLayoutEffect(() => {
    const target = scrollRestoreTarget.current;
    if (target <= 0) return;
    const scrollEl = getScrollEl();
    if (!scrollEl) return;
    if (scrollEl.scrollHeight >= target + scrollEl.clientHeight) {
      restoreScroll(scrollEl, target);
      scrollRestoreTarget.current = 0;
      scrollRestorationActive.current = false;
    }
  }, [resultEntries]); // eslint-disable-line react-hooks/exhaustive-deps

  // Callback para que ResultEntry guarde el scroll antes de navegar al detalle
  const handleBeforeNavigate = useCallback(() => {
    const scrollEl = getScrollEl();
    sessionStorage.setItem('home_scroll', String(scrollEl?.scrollTop || 0));
  }, []);

  const loadMoreData = useCallback(async () => {
    // Evitar cargar si ya está cargando o no hay más páginas
    if (isLoadingRef.current || !hasMoreRef.current) {
      return;
    }

    const pageToLoad = currentPageRef.current;
    
    // Evitar cargar la misma página dos veces
    if (pageToLoad === lastLoadedPageRef.current) {
      console.log('⚠️ Intentando cargar la misma página:', pageToLoad, 'Ignorando...');
      return;
    }

    // Marcar esta página como "en proceso de carga" ANTES de hacer la petición
    // Esto evita que se intente cargar la misma página dos veces simultáneamente
    lastLoadedPageRef.current = pageToLoad;
    setIsLoading(true);
    
    try {
      // Construir filtros para la petición usando los valores actuales de los refs
      let filtersToUse;
      
      if (currentFiltersRef.current) {
        // Actualizar la página en los filtros activos
        filtersToUse = {
          ...currentFiltersRef.current,
          page: pageToLoad, // Ya es 0-based
          size: 20
        };
      } else {
        // Búsqueda por defecto sin filtros
        filtersToUse = {
          page: pageToLoad,
          size: 20
        };
      }

      console.log('📄 Cargando página:', pageToLoad, 'Filtros:', filtersToUse);
      
      const searchResults = await searchService.searchVehicles(filtersToUse);
      
      console.log('✅ Datos cargados:', {
        contentLength: searchResults.content?.length || 0,
        totalPages: searchResults.totalPages,
        currentPage: searchResults.number,
        last: searchResults.last,
        hasMore: !searchResults.last
      });
      
      // Agregar nuevos resultados a los existentes (solo los que tienen imágenes)
      const newItems = (searchResults.content) || [];
      setResultEntries(prev => [...prev, ...newItems]);
      setAllResultEntries(prev => [...prev, ...newItems]);
      
      // Actualizar estados de paginación
      setTotalPages(searchResults.totalPages || 0);
      setHasMore(!searchResults.last); // last: false significa que hay más páginas
      
      // Actualizar página para la próxima carga (0-based)
      // La API devuelve searchResults.number que es la página que acabamos de cargar
      // La siguiente página será searchResults.number + 1
      const nextPage = searchResults.number + 1;
      
      // Actualizar el ref ANTES de actualizar el estado para evitar race conditions
      currentPageRef.current = nextPage;
      setCurrentPage(nextPage);
      
      // Marcar que la carga inicial está completa solo después de actualizar el ref
      // Usar un delay para asegurar que el DOM se haya actualizado antes de activar el observer
      if (!initialLoadDoneRef.current) {
        initialLoadDoneRef.current = true;
        // Usar setTimeout para actualizar el estado y permitir que el DOM se actualice
        setTimeout(() => {
          setInitialLoadDone(true); // Actualizar estado para que el observer se cree
        }, 300);
      }
      
      // Si hay filtros activos, actualizar la página en los filtros
      if (currentFiltersRef.current) {
        setCurrentFilters(prev => ({
          ...prev,
          page: nextPage
        }));
      }
      
    } catch (error) {
      console.error('❌ Error al cargar más datos:', error);
      // Fallback al método anterior
      try {
        let data = await publicacionService.getItems();
        setResultEntries(prev => [...prev, ...(data.content || [])]);
        setAllResultEntries(prev => [...prev, ...(data.content || [])]);
        setCurrentPage(prev => prev + 1);
      } catch (fallbackError) {
        console.error('❌ Error en fallback:', fallbackError);
        setHasMore(false); // Si falla, no intentar cargar más
      }
    } finally {
      setIsLoading(false);
    }
  }, []); // Sin dependencias, usa refs para acceder a valores actuales

  const handleFilter = (filterData) => {
    _c.results = null; // Limpiar caché al aplicar nuevos filtros

    // Si viene del nuevo servicio de búsqueda con resultados
    if (filterData.results !== undefined) {
      console.log('✅ Usando nuevo servicio de búsqueda');
      console.log('📊 Resultados:', {
        totalElements: filterData.totalElements,
        totalPages: filterData.totalPages,
        currentPage: filterData.currentPage,
        resultsLength: filterData.results?.length || 0
      });
      
      if (filterData.error) {
        console.error('❌ Error en búsqueda:', filterData.error);
        // Mostrar mensaje de error al usuario
        alert(`Error en la búsqueda: ${filterData.error}`);
        return;
      }
      
      // Resetear estados cuando se aplican nuevos filtros
      // filterData.currentPage es 1-indexed: 1 = primera página ya cargada
      const currentPageNum = filterData.currentPage || 1;
      const nextPage0 = currentPageNum; // próxima página en 0-indexed
      const lastLoaded0 = currentPageNum - 1; // última página cargada en 0-indexed
      const hasMorePages = currentPageNum < (filterData.totalPages || 0);

      setResultEntries(filterData.results || []);
      setAllResultEntries(filterData.results || []);
      setCurrentPage(nextPage0);
      setTotalPages(filterData.totalPages || 0);
      setHasMore(hasMorePages);

      // Actualizar refs también
      currentPageRef.current = nextPage0;
      hasMoreRef.current = hasMorePages;
      isLoadingRef.current = false;
      lastLoadedPageRef.current = lastLoaded0; // página 0 ya fue cargada
      initialLoadDoneRef.current = true;
      setInitialLoadDone(true);
      
      // Guardar los filtros activos para cargar más páginas con los mismos filtros
      // Actualizar marca seleccionada para reflejarla en el dropdown
      setSelectedMaker(filterData.filters?.maker || '');

      if (filterData.filters) {
        const newFilters = {
          ...filterData.filters,
          page: 0, // Resetear página cuando se aplican filtros
          size: 20
        };
        setCurrentFilters(newFilters);
        currentFiltersRef.current = newFilters;
        updateUrl(newFilters);
      } else {
        // Si no hay filtros en filterData, limpiar currentFilters
        setCurrentFilters(null);
        currentFiltersRef.current = null;
      }
      
      return;
    }

    console.log('🔄 Usando filtrado local (fallback)');
    // Fallback al filtrado local (método anterior)
    let filtered = [...resultAllEntries];

    // Marca
    if (filterData.brand && filterData.brand !== "Todos") {
      filtered = filtered.filter(
        (entry) => entry.brand?.name?.toLowerCase() === filterData.brand.toLowerCase()
      );
    }

    // Modelo
    if (filterData.model && filterData.model !== "Todos") {
      filtered = filtered.filter(
        (entry) => entry.model?.toLowerCase() === filterData.model.toLowerCase()
      );
    }

    // Año desde
    if (filterData.yearFrom) {
      filtered = filtered.filter(
        (entry) => parseInt(entry.year) >= parseInt(filterData.yearFrom)
      );
    }

    // Año hasta
    if (filterData.yearTo) {
      filtered = filtered.filter(
        (entry) => parseInt(entry.year) <= parseInt(filterData.yearTo)
      );
    }

    // Precio desde
    if (filterData.priceFrom) {
      filtered = filtered.filter(
        (entry) => entry.price >= parseInt(filterData.priceFrom)
      );
    }

    // Precio hasta
    if (filterData.priceTo) {
      filtered = filtered.filter(
        (entry) => entry.price <= parseInt(filterData.priceTo)
      );
    }

    // Tipo combustible
    if (filterData.fuelType && filterData.fuelType !== "Todos") {
      filtered = filtered.filter(
        (entry) => entry.fuelType?.toLowerCase() === filterData.fuelType.toLowerCase()
      );
    }

    // Región
    if (filterData.region && filterData.region !== "Todos") {
      filtered = filtered.filter(
        (entry) => entry.region?.toLowerCase() === filterData.region.toLowerCase()
      );
    }

    // Ciudad
    if (filterData.city && filterData.city !== "Todos") {
      filtered = filtered.filter(
        (entry) => entry.city?.toLowerCase() === filterData.city.toLowerCase()
      );
    }

    // Transmisión
    if (filterData.transmission && filterData.transmission !== "Todos") {
      filtered = filtered.filter(
        (entry) => entry.transmission?.toLowerCase() === filterData.transmission.toLowerCase()
      );
    }

    setResultEntries(filtered);
  };


  // Guardar resultados en caché módulo cada vez que cambian
  useEffect(() => {
    if (resultEntries.length > 0) {
      _c.results = resultEntries;
      _c.filters = currentFilters;
      _c.page = currentPage;
      _c.hasMore = hasMore;
      _c.totalPages = totalPages;
    }
  }, [resultEntries, currentPage, hasMore, totalPages, currentFilters]);

  // Cargar datos iniciales al montar el componente
  useEffect(() => {
    // Si ya restauramos desde caché módulo, no hacer nada más
    if (hasCache) return;

    // Sin caché: intentar restaurar desde URL params
    const urlFilters = getFiltersFromUrl();
    const hasUrlFilters = Object.keys(urlFilters).length > 0;

    if (hasUrlFilters) {
      const filters = { ...urlFilters, page: 0, size: 20 };
      setCurrentFilters(filters);
      currentFiltersRef.current = filters;
      setSelectedMaker(urlFilters.maker || '');
      searchService.searchVehicles(filters).then(results => {
        setResultEntries(results.content || []);
        setAllResultEntries(results.content || []);
        setCurrentPage(1);
        setTotalPages(results.totalPages || 0);
        setHasMore(!results.last);
        currentPageRef.current = 1;
        hasMoreRef.current = !results.last;
        isLoadingRef.current = false;
        lastLoadedPageRef.current = 0;
        setTimeout(() => { setInitialLoadDone(true); initialLoadDoneRef.current = true; }, 300);
      }).catch(err => console.error('Error al restaurar búsqueda desde URL:', err));
    } else {
      // Carga normal desde cero
      if (resultEntries.length === 0 && !isLoadingRef.current && lastLoadedPageRef.current !== 0) {
        loadMoreData();
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // IntersectionObserver para detectar cuando el usuario llega al final
  useEffect(() => {
    // Solo crear el observer después de que se haya completado la carga inicial
    if (!initialLoadDone) {
      return;
    }

    let observer = null;
    const currentRef = loadMoreRef.current;

    // Pequeño delay para asegurar que el DOM y los refs estén actualizados
    const timeoutId = setTimeout(() => {
      observer = new IntersectionObserver(
        (entries) => {
          // Si el elemento está visible, verificar usando refs (valores actuales)
          // Verificar que no estemos cargando y que haya más páginas
          if (entries[0].isIntersecting &&
              !scrollRestorationActive.current &&
              hasMoreRef.current &&
              !isLoadingRef.current &&
              initialLoadDoneRef.current) {
            console.log('🔽 Llegó al final, cargando más datos...');
            loadMoreData();
          }
        },
        {
          root: null, // Usar viewport como root
          rootMargin: '100px', // Cargar cuando esté a 100px del final
          threshold: 0.1
        }
      );

      if (currentRef) {
        observer.observe(currentRef);
      }
    }, 200); // Delay adicional para asegurar que todo esté listo

    return () => {
      clearTimeout(timeoutId);
      if (observer && currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [loadMoreData, initialLoadDone]); // Re-ejecutar cuando cambie loadMoreData o initialLoadDone

  let hiddenColumnRef = useRef(null);
  let fixedColumnRef = useRef(null);
  let scrollableContentRef = useRef(null);
  let observerRefValue = hiddenColumnRef.current

  useEffect(() => {
    const observer = new ResizeObserver(entries => {
      let width = Math.floor(entries[0].contentRect.width)


      if (fixedColumnRef.current == null) { return; }
      if (width > 0) {
        fixedColumnRef.current.style.width = width - 20 + 'px'
        scrollableContentRef.current.style.width = width - 60 + 'px'
        fixedColumnRef.current.style.position = 'fixed'
      }
    })
    observer.observe(hiddenColumnRef.current)
    return () => observerRefValue && observer.unobserve(observerRefValue)
  })

  return (
    <>
      <div ref={fixedColumnRef} className='d-none d-md-block options-bar-fixed'>
        <div className="flex-container">
          <Scrollbars>
            <div className='scrollable'>
              <div></div>
              <div ref={scrollableContentRef}>
                <br />
                <NavigationMenu />
                <Filters brands={filterData["brands"]} models={resultAllEntries} onFilter={handleFilter} searchQuery={searchText} selectedMaker={selectedMaker}></Filters>
                <BrandsFilter brands={filterData["brands"]} onFilter={handleFilter} selectedMaker={selectedMaker} />
                
              </div>
            </div>
          </Scrollbars>

        </div>
      </div>

      <Row>
        <Col ref={hiddenColumnRef} xs={4} className='d-none d-md-block'></Col>
        <Col xs={12} md={8}>
          <div className='container-home-feed '>
            {
              resultEntries.map((item, key) =>
                <section key={key} >
                  <br />
                  <ResultEntry entry={item} onBeforeNavigate={handleBeforeNavigate} />
                  {resultEntries.length !== key + 1 &&
                    <Row className="d-none d-md-flex">
                      <Col>
                        <hr />
                      </Col>
                    </Row>
                  }
                </section>
              )
            }
            {!isLoading && initialLoadDone && resultEntries.length === 0 && (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: '#888' }}>
                <p style={{ fontSize: '1.1rem' }}>No se encontraron vehículos para tu búsqueda.</p>
                <p style={{ fontSize: '0.9rem' }}>Intenta con otros términos o elimina algunos filtros.</p>
              </div>
            )}
            {/* Elemento invisible que dispara la carga cuando se hace scroll hasta él */}
            <div ref={loadMoreRef} style={{ height: '20px', marginTop: '20px' }}>
              {isLoading && (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <p>Cargando más vehículos...</p>
                </div>
              )}
              {!hasMore && resultEntries.length > 0 && (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <p>No hay más vehículos para mostrar</p>
                </div>
              )}
            </div>
          </div>
        </Col>
      </Row>
    </>
  )
};

export default Home;